import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Input, Space, Spin, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  getMpSyncWatermarkHistory,
  getMpSyncWatermarkLog,
  getMpSyncWatermarks,
  MpSyncWatermarkDto,
  MpSyncWatermarkLogDto,
} from "api/MpSyncWatermarks";

const { Title, Text } = Typography;

type ReservedChangeRow = {
  article: string;
  alias?: string | null;
  from: number;
  to: number;
  warehouse?: string | null;
};

const mpLabel = (mp: string) => {
  if (mp === "wildberries") return "Wildberries";
  if (mp === "ozon") return "Ozon";
  if (mp === "1c") return "1С";
  return mp;
};

const kindLabel = (kind: string) => {
  if (kind === "reserved") return "Резервы";
  if (kind === "warehouse") return "Остатки склада МП";
  if (kind === "remnant_1c") return "Остатки 1С";
  if (kind === "send") return "Отправка на МП";
  if (kind === "sheets_listing") return "Таблица FBS";
  return kind;
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const ageMs = (iso: string) => {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return Number.POSITIVE_INFINITY;
  return Date.now() - t;
};

const ageTag = (iso: string) => {
  const ms = ageMs(iso);
  if (!Number.isFinite(ms)) return <Tag>—</Tag>;
  const min = Math.round(ms / 60000);
  if (min < 5) return <Tag color="success">{min < 1 ? "только что" : `${min} мин`}</Tag>;
  if (min < 60) return <Tag color="processing">{min} мин</Tag>;
  const h = Math.round(min / 60);
  if (h < 24) return <Tag color="warning">{h} ч</Tag>;
  return <Tag color="error">{Math.round(h / 24)} д</Tag>;
};

const readChanges = (
  stats: Record<string, unknown> | null | undefined
): ReservedChangeRow[] => {
  const raw = stats?.changes;
  if (!Array.isArray(raw)) return [];
  const out: ReservedChangeRow[] = [];
  for (const c of raw) {
    if (!c || typeof c !== "object") continue;
    const row = c as Record<string, unknown>;
    if (typeof row.article !== "string") continue;
    const from = Number(row.from);
    const to = Number(row.to);
    if (!Number.isFinite(from) || !Number.isFinite(to)) continue;
    out.push({
      article: row.article,
      alias: typeof row.alias === "string" ? row.alias : null,
      from,
      to,
      warehouse: typeof row.warehouse === "string" ? row.warehouse : null,
    });
  }
  return out;
};

const changesHintCount = (
  row: MpSyncWatermarkDto,
  articleFilter: string
): number => {
  const all = readChanges(row.stats);
  if (articleFilter) {
    return all.filter((c) => matchesArticle(c, articleFilter)).length;
  }
  if (all.length) return all.length;
  const cc = Number(row.stats?.changeCount);
  if (Number.isFinite(cc) && cc > 0) return cc;
  return 0;
};

const matchesArticle = (c: ReservedChangeRow, q: string) => {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return (
    c.article.toLowerCase().includes(needle) ||
    (c.alias || "").toLowerCase().includes(needle)
  );
};

const ChangesTable: React.FC<{
  changes: ReservedChangeRow[];
  articleFilter?: string;
  emptyHint?: string;
}> = ({ changes: all, articleFilter, emptyHint }) => {
  const q = articleFilter?.trim() || "";
  const changes = q ? all.filter((c) => matchesArticle(c, q)) : all;

  if (!all.length) {
    return <Text type="secondary">{emptyHint || "Список изменений пуст"}</Text>;
  }
  if (q && !changes.length) {
    return (
      <Text type="secondary">
        В этом прогоне артикул «{q}» не найден в списке изменений
      </Text>
    );
  }

  const hasWarehouse = all.some((c) => c.warehouse);

  return (
    <div>
      {q && (
        <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
          Совпадения по «{q}»: {changes.length} из {all.length}
        </Text>
      )}
      <Table
        size="small"
        pagination={changes.length > 100 ? { pageSize: 100 } : false}
        rowKey={(c, i) => `${c.article}:${i}`}
        dataSource={changes}
        columns={[
          ...(hasWarehouse
            ? [
                {
                  title: "Склад",
                  dataIndex: "warehouse",
                  width: 200,
                  render: (v: string | null | undefined) => v || "—",
                },
              ]
            : []),
          { title: "Артикул", dataIndex: "article", width: 160 },
          {
            title: "Alias",
            dataIndex: "alias",
            width: 160,
            render: (v: string | null | undefined) => v || "—",
          },
          { title: "Было", dataIndex: "from", width: 80 },
          { title: "Стало", dataIndex: "to", width: 80 },
        ]}
      />
    </div>
  );
};

const LatestExpand: React.FC<{
  row: MpSyncWatermarkDto;
  articleFilter?: string;
}> = ({ row, articleFilter }) => (
  <ChangesTable
    changes={readChanges(row.stats)}
    articleFilter={articleFilter}
    emptyHint={
      row.kind === "warehouse"
        ? "Список пуст (остаток на МП не менялся или прогон без детализации)"
        : "Список изменений пуст"
    }
  />
);

/** История отдаётся lite без changes — подгружаем по id при раскрытии. */
const HistoryExpand: React.FC<{
  row: MpSyncWatermarkLogDto;
  articleFilter?: string;
}> = ({ row, articleFilter }) => {
  const omitted = Boolean(row.stats?.changesOmitted);
  const [full, setFull] = useState<MpSyncWatermarkLogDto | null>(
    omitted ? null : row
  );
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!omitted) {
      setFull(row);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr(null);
    getMpSyncWatermarkLog(row.id)
      .then((data) => {
        if (!cancelled) setFull(data);
      })
      .catch(() => {
        if (!cancelled) setErr("Не удалось загрузить детализацию");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [row, omitted]);

  if (loading) return <Spin size="small" />;
  if (err) return <Text type="danger">{err}</Text>;
  if (!full) return <Text type="secondary">Нет данных</Text>;
  return (
    <ChangesTable
      changes={readChanges(full.stats)}
      articleFilter={articleFilter}
    />
  );
};

const MpSyncWatermarksPage: React.FC = () => {
  const [rows, setRows] = useState<MpSyncWatermarkDto[]>([]);
  const [history, setHistory] = useState<MpSyncWatermarkLogDto[]>([]);
  const [historyHours, setHistoryHours] = useState(24);
  const [articleDraft, setArticleDraft] = useState("");
  const [article, setArticle] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = article || undefined;
      const [latest, hist] = await Promise.all([
        getMpSyncWatermarks(q),
        getMpSyncWatermarkHistory(historyHours, q),
      ]);
      setRows(latest);
      setHistory(hist.items);
      setHistoryHours(hist.hours);
    } catch {
      setError("Не удалось загрузить watermarks.");
      setRows([]);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [article, historyHours]);

  useEffect(() => {
    load();
  }, [load]);

  const applyArticleSearch = (value?: string) => {
    setArticle((value ?? articleDraft).trim());
  };

  const sorted = useMemo(() => {
    const mpOrder = ["1c", "wildberries", "ozon"];
    const kindOrder = ["remnant_1c", "reserved", "warehouse", "send"];
    return [...rows].sort((a, b) => {
      const ai = mpOrder.indexOf(a.marketplace);
      const bi = mpOrder.indexOf(b.marketplace);
      if (ai !== bi) return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
      const aki = kindOrder.indexOf(a.kind);
      const bki = kindOrder.indexOf(b.kind);
      if (aki !== bki) return (aki < 0 ? 99 : aki) - (bki < 0 ? 99 : bki);
      return a.kind.localeCompare(b.kind);
    });
  }, [rows]);

  const listHint = (r: MpSyncWatermarkDto) => {
    const n = changesHintCount(r, article);
    if (n > 0) return <Text>{n} арт. — раскройте строку</Text>;
    if (!article && r.rowsChanged > 0) {
      return (
        <Text type="secondary">{r.rowsChanged} изм. без списка</Text>
      );
    }
    return <Text type="secondary">нет</Text>;
  };

  const latestColumns: ColumnsType<MpSyncWatermarkDto> = [
    {
      title: "Маркетплейс",
      dataIndex: "marketplace",
      render: (v: string) => mpLabel(v),
    },
    {
      title: "Тип",
      dataIndex: "kind",
      render: (v: string) => kindLabel(v),
    },
    {
      title: "as_of",
      dataIndex: "asOf",
      render: (v: string) => formatDate(v),
    },
    {
      title: "Давность",
      dataIndex: "asOf",
      key: "age",
      render: (v: string) => ageTag(v),
    },
    {
      title: "finished_at",
      dataIndex: "finishedAt",
      render: (v: string) => formatDate(v),
    },
    {
      title: "Изменено",
      key: "changed",
      render: (_, r) => `${r.rowsChanged} / ${r.rowsTotal}`,
    },
    {
      title: "Список",
      key: "changesHint",
      render: (_, r) => listHint(r),
    },
  ];

  const historyColumns: ColumnsType<MpSyncWatermarkLogDto> = [
    {
      title: "Время",
      dataIndex: "finishedAt",
      width: 180,
      render: (v: string) => formatDate(v),
    },
    {
      title: "Маркетплейс",
      dataIndex: "marketplace",
      width: 140,
      render: (v: string) => mpLabel(v),
    },
    {
      title: "Тип",
      dataIndex: "kind",
      width: 140,
      render: (v: string) => kindLabel(v),
    },
    {
      title: "Изменено",
      key: "changed",
      width: 120,
      render: (_, r) => `${r.rowsChanged} / ${r.rowsTotal}`,
    },
    {
      title: "Список",
      key: "changesHint",
      render: (_, r) => listHint(r),
    },
  ];

  return (
    <div style={{ paddingTop: 16, paddingBottom: 32 }}>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            MP Sync Watermarks
          </Title>
          <Text type="secondary">
            Последние прогоны: 1С, резервы, склад МП, отправка — и история за сутки
          </Text>
        </div>
        <Button onClick={load} loading={loading}>
          Обновить
        </Button>
      </Space>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Артикул или alias"
          value={articleDraft}
          onChange={(e) => setArticleDraft(e.target.value)}
          onSearch={applyArticleSearch}
          style={{ width: 280 }}
          enterButton="Найти"
        />
        {article && (
          <Tag
            closable
            onClose={() => {
              setArticleDraft("");
              setArticle("");
            }}
          >
            фильтр: {article}
          </Tag>
        )}
      </Space>

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Title level={5} style={{ marginTop: 0 }}>
        Текущие{article ? ` с «${article}»` : ""}
      </Title>
      <Table
        key={`latest:${article || "all"}`}
        rowKey={(r) => `${r.marketplace}:${r.kind}`}
        loading={loading}
        columns={latestColumns}
        dataSource={sorted}
        pagination={false}
        style={{ marginBottom: 32 }}
        expandable={{
          expandedRowRender: (r) => (
            <LatestExpand row={r} articleFilter={article} />
          ),
          rowExpandable: () => true,
          defaultExpandAllRows: Boolean(article),
        }}
        locale={{
          emptyText: article
            ? `Нет текущих watermarks с артикулом «${article}»`
            : "Нет данных — после sync появятся записи",
        }}
      />

      <Title level={5}>
        История за {historyHours} ч{article ? ` · «${article}»` : ""}{" "}
        <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>
          ({history.length} записей)
        </Text>
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        {article
          ? "Показаны прогоны, где артикул/alias есть в stats.changes"
          : "Раскройте строку для детализации по артикулам (подгружается отдельно)"}
      </Text>
      <Table
        key={`history:${article || "all"}`}
        rowKey={(r) => String(r.id)}
        loading={loading}
        columns={historyColumns}
        dataSource={history}
        pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `всего ${t}` }}
        expandable={{
          expandedRowRender: (r) => (
            <HistoryExpand row={r} articleFilter={article} />
          ),
          rowExpandable: () => true,
        }}
        locale={{
          emptyText: article
            ? `Нет прогонов с артикулом «${article}» за ${historyHours} ч`
            : "Нет прогонов за сутки — появятся после следующего sync",
        }}
      />
    </div>
  );
};

export default MpSyncWatermarksPage;
